import {ASSET_FIELD_KEYS} from './asset-type.descriptor';
import {findAssetTypeDescriptor, getAssetFieldKeys} from './asset-type.registry';

import type {AssetAttributes, AssetFieldKey, AssetTitleSource} from './asset-type.descriptor';
import type {AssetType} from '@sollapay/enums';

/** One populated field, carrying the token a caller resolves to copy — no i18n in this package. */
export interface AssetFieldEntry {
  key: AssetFieldKey;
  labelToken: string;
  value: string;
}

export interface CollectAssetFieldsOptions {
  /** Fields shown elsewhere already — typically the ones a title consumed. */
  exclude?: AssetFieldKey[];
  limit?: number;
}

/**
 * The type's populated fields in descriptor order — the single walk every asset label is built
 * from. Empty for an unrecognized assetType, so a stale bundle degrades rather than throws.
 */
export function collectAssetFields(
  assetType: AssetType,
  attributes: AssetAttributes,
  {exclude, limit}: CollectAssetFieldsOptions = {}
): AssetFieldEntry[] {
  const descriptor = findAssetTypeDescriptor(assetType);
  if (!descriptor) return [];

  const entries: AssetFieldEntry[] = [];
  for (const field of descriptor.fields) {
    if (limit !== undefined && entries.length >= limit) break;
    if (exclude?.includes(field.key)) continue;

    const value = normalizeAssetFieldValue(attributes[field.key]);
    if (value) entries.push({key: field.key, labelToken: field.labelToken, value});
  }
  return entries;
}

export interface ResolvedAssetTitle {
  source: AssetTitleSource;
  /** The fields the title shows, so a caller can drop them from whatever it renders next. */
  consumed: AssetFieldKey[];
  /** Normalized, in `consumed` order. */
  values: string[];
}

/**
 * The first title candidate whose every field is filled. Undefined when none qualifies, which
 * leaves the caller to fall back to the type's own name.
 */
export function resolveAssetTitle(
  assetType: AssetType,
  attributes: AssetAttributes
): ResolvedAssetTitle | undefined {
  const descriptor = findAssetTypeDescriptor(assetType);
  if (!descriptor) return undefined;

  for (const {source, keys} of descriptor.titleCandidates) {
    const values = keys.map(key => normalizeAssetFieldValue(attributes[key]));
    if (values.every(value => value !== undefined)) {
      return {source, consumed: keys, values: values as string[]};
    }
  }
  return undefined;
}

/** `'0'` is a real ground floor and `'-2/17'` a real parking bay — only blank counts as absent. */
export function normalizeAssetFieldValue(value: string | null | undefined): string | undefined {
  return value?.trim().replace(/\s+/g, ' ') || undefined;
}

/**
 * Nulls every field the type does not accept. A partial update retains omitted columns, so
 * without this a type change leaves the previous type's values behind on the row — invisible to
 * validation, which only ever sees the incoming patch.
 */
export function clearUnsupportedAssetFields<T extends AssetAttributes>(
  assetType: AssetType,
  attributes: T
): T {
  const accepted = new Set<AssetFieldKey>(getAssetFieldKeys(assetType));

  const cleared = {...attributes};
  for (const key of ASSET_FIELD_KEYS) {
    if (!accepted.has(key)) (cleared as AssetAttributes)[key] = null;
  }
  return cleared;
}
